import {
	ConnectionRelation,
	ConnectableObject,
} from '@ikea-aoa/component-view-3d/lib/main/connection-manager';
import {
	ConnectionManager,
	TransformVector,
	GridTools,
	SceneObject,
	TransformPosition,
} from '@ikea-aoa/component-view-3d';
import { ModuleConnections, ConnectedModule } from './ModuleConnectionManager/ModuleConnections';
import { Transform, TransformRotation } from '@ikea-aoa/elements-3d';

/**
 * Module Connection Manager Class.
 */
export class ModuleConnectionManager {
	constructor(
		public connectionManager: ConnectionManager,
		public sceneItems: Map<string, ConnectableObject>,
		private gridTools: GridTools,
		private isModular: boolean
	) {}
	private moduleConnections = new ModuleConnections(this.sceneItems);

	public connect(child: SceneObject, parent: SceneObject, relation: ConnectionRelation): void {
		this.connectionManager.add(child, parent, relation);
		if (this.shouldConnectAsChild(child)) {
			// Perfom regular child -> parent connection.
			this.connectionManager.connect(child, parent, relation);
			if (child.node && parent.node) {
				child.node.setParent(parent.node, child.transform.position, child.transform.rotation);
			}
		} else {
			// Perfom connection between two modules.
			this.connectModules(child, parent, relation);
		}
	}

	public disconnect(sceneObject: SceneObject): void {
		this.connectionManager.remove(sceneObject);
		this.connectionManager.disconnect(sceneObject);
		if (sceneObject.node) {
			sceneObject.node.disconnect(sceneObject.transform.position, sceneObject.transform.rotation);
		}
		if (this.moduleConnections.has(sceneObject.id)) {
			this.moduleConnections.delete(sceneObject.id);
		}
	}

	public getConnectedModules(sceneItem: SceneObject | string): Array<SceneObject> {
		const id = typeof sceneItem == 'string' ? sceneItem : sceneItem.id;
		return this.moduleConnections.get(id);
	}

	public hasConnectedModules(sceneItem: SceneObject | string): boolean {
		const id = typeof sceneItem == 'string' ? sceneItem : sceneItem.id;
		return this.moduleConnections.has(id);
	}

	public getAllConnectedModules(sceneItemId: string): Array<ConnectedModule> {
		return this.moduleConnections.getModules(sceneItemId);
	}

	public getChildren(sceneItem: SceneObject | string): Array<SceneObject> {
		const id = typeof sceneItem == 'string' ? sceneItem : sceneItem.id;
		let children = new Array<SceneObject>();
		this.connectionManager.getChildren(id).forEach(function (id) {
			const sceneItem = this.sceneItem.get(id);
			if (sceneItem) {
				children = sceneItem.filter((item) => this.connectionManager.isAddAsChild(item.articleId));
			}
		});
		return children;
	}

	private resetSceneConnections(): void {
		this.sceneItems.forEach((sceneItem) => {
			this.disconnect(sceneItem);
		});
	}

	public connectAll(): void {
		this.resetSceneConnections();

		const allConnections: {
			child?: SceneObject;
			parent?: SceneObject;
			relation?: ConnectionRelation;
		}[] = [];
		const currentlyConnected: string[][] = [];

		this.sceneItems.forEach((sceneItem) => {
			const collidingObjects = this.getCollidingObjects(sceneItem);

			collidingObjects.forEach((collidingObject) => {
				const childObject = this.connectionManager.isAddAsChild(collidingObject.articleId)
					? collidingObject
					: sceneItem;
				const parentObject = this.connectionManager.isAddAsChild(collidingObject.articleId)
					? sceneItem
					: collidingObject;
				const relation = this.getValidRelation(childObject, parentObject, 0.65);

				if (relation) {
					allConnections.push({
						child: childObject,
						parent: parentObject,
						relation: relation,
					});
				}
			});
		});

		allConnections.forEach((connection) => {
			const alreadyConnected = currentlyConnected.some((currentConnection) => {
				if (connection && connection.child && connection.parent) {
					return (
						currentConnection.includes(connection.child.id) &&
						currentConnection.includes(connection.parent.id)
					);
				}
				return false;
			});

			if (
				!alreadyConnected &&
				connection &&
				connection.child &&
				connection.parent &&
				connection.relation
			) {
				this.connect(connection.child, connection.parent, connection.relation);
				currentlyConnected.push([connection.child.id, connection.parent.id]);
			}
		});
	}

	public getAvailableConnectionPoints(sceneItem: SceneObject | string): Array<string> {
		const sceneObject = typeof sceneItem == 'string' ? this.sceneItems.get(sceneItem) : sceneItem;

		if (sceneObject) {
			const model = this.connectionManager.getModelData(sceneObject.articleId);
			if (!model) {
				return [];
			}
			const { connections } = model;
			const connectedModules = this.moduleConnections.getModules(sceneObject.id);

			return Array.from(Object.keys(connections)).filter(
				(key) => !connectedModules.find((module) => module.connection.from == key)
			);
		}
		return [];
	}

	public clearConnections(): void {
		this.moduleConnections.clear();
	}

	private connectModules(
		child: SceneObject,
		parent: SceneObject,
		relation: ConnectionRelation
	): void {
		const worldTransform = this.connectionManager.getConnectedWorldTransform(
			child.articleId,
			parent,
			relation
		);
		child.transform = worldTransform;

		this.getPossibleConnections(child).forEach((connectionObject) => {
			this.moduleConnections.add(child.id, connectionObject);
		});

		if (child.node) {
			child.node.setLocalPosition(worldTransform.position as TransformPosition);
			child.node.assembly.localRotation = worldTransform.rotation as TransformRotation;
		}
	}

	private getPossibleConnections(sceneObject: SceneObject): Array<ConnectedModule> {
		const colliding = this.getCollidingObjects(sceneObject);
		const connected = colliding.reduce((acc: ConnectedModule[], collidingObject) => {
			const relation = this.getValidRelation(sceneObject, collidingObject);
			if (relation) {
				const from = relation.child;
				const to = relation.parent;
				acc.push(new ConnectedModule(from, to, collidingObject));
			}

			return acc;
		}, []);
		return connected;
	}

	public getValidRelation(
		child: SceneObject,
		parent: SceneObject,
		threshold = 0.25
	): ConnectionRelation | null {
		const childTransform = this.connectionManager.getWorldTransform(child);

		const childModel = this.connectionManager.getModelData(child.articleId);
		const parentModel = this.connectionManager.getModelData(parent.articleId);
		if (childModel && parentModel) {
			const relations = this.connectionManager.getRelations(childModel, parentModel);
			const relation = relations.find((relation) => {
				const connectedTransform = this.connectionManager.getConnectedWorldTransform(
					child.articleId,
					parent,
					relation
				);

				if (
					childTransform &&
					childTransform.position &&
					connectedTransform &&
					connectedTransform.position &&
					childTransform.rotation &&
					connectedTransform.rotation
				) {
					const positionIsEqual = TransformVector.isEqualWithEpsilon(
						{ x: childTransform.position.x, y: 0, z: childTransform.position.z },
						{ x: connectedTransform.position.x, y: 0, z: connectedTransform.position.z },
						threshold
					);

					let rotationIsEqual = TransformVector.isEqualWithEpsilon(
						childTransform.rotation,
						connectedTransform.rotation,
						0.1
					);

					if (!rotationIsEqual) {
						rotationIsEqual = this.checkMirroredRotation(childTransform, connectedTransform);
					}

					return positionIsEqual && rotationIsEqual;
				}
				return null;
			});
			return relation || null;
		}

		return null;
	}

	private shouldConnectAsChild(child): boolean {
		return this.isModular
			? this.connectionManager.isChildOnly(child.articleId)
			: this.connectionManager.isAddAsChild(child.articleId);
	}

	private getCollidingObjects(sceneObject: SceneObject): Array<SceneObject> {
		const children = this.connectionManager.getChildren(sceneObject.id);
		const gridBoxes = this.gridTools.createGridBoxes(this.sceneItems, -0.5, [
			sceneObject.id,
			...children,
		]);
		const itemGridBox = this.gridTools.createGridBoxFromObject(sceneObject, 'default', -1);

		const intersections = this.gridTools.checkBoxIntersections(itemGridBox, gridBoxes);
		const collidingObjects = new Array<SceneObject>();
		const sceneItems = this.sceneItems;
		intersections.forEach(function (id) {
			const sceneItem = sceneItems.get(id);
			if (sceneItem) {
				collidingObjects.push(sceneItem);
			}
		});
		return collidingObjects;
	}

	private getNormalizedDegRotation(rad: number): number {
		return Math.round(rad * (180 / Math.PI)) % 360;
	}

	private getMirroredRotation(deg: number): number {
		switch (deg) {
			case 0:
				return 360;
			case 90:
				return -270;
			case 180:
				return -180;
			case 270:
				return -90;
			case 360:
				return 0;
			case -90:
				return 270;
			case -180:
				return 180;
			case -270:
				return 90;
			default:
				return deg;
		}
	}

	private checkMirroredRotation(childTransform: Transform, connectedTransform: Transform): boolean {
		if (connectedTransform.rotation && childTransform.rotation && childTransform.rotation.y) {
			const normalizedRotation = this.getNormalizedDegRotation(childTransform.rotation.y);
			const mirroredRotation = this.getMirroredRotation(normalizedRotation);
			return TransformVector.isEqualWithEpsilon(
				{
					x: childTransform.rotation.x,
					y: mirroredRotation * (Math.PI / 180),
					z: childTransform.rotation.z,
				},
				connectedTransform.rotation,
				0.1
			);
		}
		return false;
	}
}
